/**
 * Composant d'affichage des résultats pour l'entretien structuré
 * Affiche les scores par compétence et sous-compétence (hiérarchique)
 * Supporte l'ancien format (sans sous_compétence) et le nouveau format (avec sous_compétence)
 */

import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import {
  ArrowRightLeft,
  Bot,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardPaste,
  Clock,
  AlertTriangle,
  Eye,
  MessageSquare,
  Mic,
  Timer,
  Trophy,
  Type,
  User,
  X,
} from "lucide-react";
import {
  calculerStatsCheating,
  estSuspect,
  formatDateTime,
  formatDuration,
  interpreterScore,
} from "@repo/utils";
import type { CheatingMeta, CheatingStats } from "@repo/utils";

// ============================================
// TYPES
// ============================================

interface EntStructCriteriaEtat {
  criteriaId: string;
  description: string;
  points: number;
  covered: boolean;
  asked: boolean;
}

interface EntStructQuestionEtat {
  questionId: string;
  competence: string;
  competence_weight?: number;
  sous_competence?: string;
  sous_competence_weight?: number;
  question_text: string;
  criteria: EntStructCriteriaEtat[];
}

// Métadonnées de transcription pour un message utilisateur
export interface EntStructTranscriptionMeta {
  caracteresTranscrits: number;
  caracteresTotal: number;
  pourcentage: number;
}

export interface EntStructMessage {
  role: "assistant" | "user";
  content: string;
  questionId?: string;
  criteriaEtat?: EntStructCriteriaEtat[];
  transcriptionMeta?: EntStructTranscriptionMeta;
  cheatingMeta?: CheatingMeta;
}

interface EntStructDonnees {
  type: "ent-struct";
  template?: string;
  threadId?: string;
  reponses: Record<string, EntStructMessage>;
  questionsEtat?: EntStructQuestionEtat[];
  questionCouranteId?: string;
  resultats?: Array<{
    jobId: string;
    app: string;
    timestamp: number;
    data: string;
  }>;
}

interface CandidatInfo {
  prenom: string;
  nom: string;
  email: string;
  dateDebut?: number;
  dateFin?: number;
  tempsPasse?: number;
}

interface EntStructResultsProps {
  donnees: EntStructDonnees;
  candidatInfo: CandidatInfo;
}

// ============================================
// STRUCTURES DE CALCUL
// ============================================

interface CritereDetail {
  criteriaId: string;
  description: string;
  points: number;
  covered: boolean;
  asked: boolean;
  questionId: string;
}

interface SousCompetenceScore {
  nom: string;
  scoreObtenu: number;
  scoreMax: number;
  criteres: CritereDetail[];
}
interface CompetenceScore {
  nom: string;
  poids: number;
  scoreObtenu: number;
  scoreMax: number;
  scorePondere: number;
  maxPondere: number;
  sousCompetences: SousCompetenceScore[];
  criteres?: CritereDetail[]; // Pour l'ancien format (sans sous-compétences)
}
interface ScoresCalcules {
  scoreGlobalObtenu: number;
  scoreGlobalMax: number;
  scoreGlobalBrut: number;
  scoreGlobalMaxBrut: number;
  competences: CompetenceScore[];
  isHierarchical: boolean; // Indique si les données ont des sous-compétences
}

// ============================================
// FONCTIONS DE CALCUL
// ============================================

/**
 * Arrondit un nombre à 2 décimales
 */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Formate le temps passé en secondes vers un format lisible
 */
/**
 * Détecte si les données contiennent des sous-compétences
 */
function hasSousCompetences(questionsEtat: EntStructQuestionEtat[]): boolean {
  return questionsEtat.some(
    (q) => q.sous_competence && q.sous_competence.trim() !== "",
  );
}

/**
 * Calcule les scores avec structure hiérarchique (compétence > sous-compétences)
 */
function calculerScoresHierarchique(
  questionsEtat: EntStructQuestionEtat[],
): ScoresCalcules {
  // Map pour grouper par compétence puis par sous-compétence
  const competencesMap = new Map<
    string,
    {
      poids: number;
      sousCompetences: Map<
        string,
        { poids: number; criteres: CritereDetail[] }
      >;
    }
  >();
  const ordreCompetences: string[] = [];

  // Parcourir toutes les questions
  for (const question of questionsEtat) {
    const competence = question.competence;
    const sousCompetence = question.sous_competence || "Général";

    // Initialiser la compétence si nécessaire
    if (!competencesMap.has(competence)) {
      competencesMap.set(competence, {
        poids: question.competence_weight ?? 1,
        sousCompetences: new Map(),
      });
      ordreCompetences.push(competence);
    }

    // Initialiser la sous-compétence si nécessaire
    const compData = competencesMap.get(competence)!;
    if (!compData.sousCompetences.has(sousCompetence)) {
      compData.sousCompetences.set(sousCompetence, {
        poids: question.sous_competence_weight ?? 1,
        criteres: [],
      });
    }

    // Ajouter les critères à la sous-compétence
    const sousCompData = compData.sousCompetences.get(sousCompetence)!;
    for (const critere of question.criteria) {
      sousCompData.criteres.push({
        criteriaId: critere.criteriaId,
        description: critere.description,
        points: critere.points,
        covered: critere.covered,
        asked: critere.asked,
        questionId: question.questionId,
      });
    }
  }

  // Calculer les scores
  const competences: CompetenceScore[] = ordreCompetences.map((nomComp) => {
    const compData = competencesMap.get(nomComp)!;

    // Convertir les sous-compétences en array
    const sousCompetencesArray: SousCompetenceScore[] = [];
    const ordreSousComp = Array.from(compData.sousCompetences.keys());

    for (const nomSousComp of ordreSousComp) {
      const sousCompData = compData.sousCompetences.get(nomSousComp)!;

      const scoreObtenu = sousCompData.criteres
        .filter((c) => c.covered)
        .reduce((sum, c) => sum + c.points, 0);

      const scoreMax = sousCompData.criteres
        .filter((c) => c.points > 0)
        .reduce((sum, c) => sum + c.points, 0);

      sousCompetencesArray.push({
        nom: nomSousComp,
        scoreObtenu,
        scoreMax,
        criteres: sousCompData.criteres,
      });
    }

    // Score de la compétence = somme des scores bruts des sous-compétences
    const scoreObtenu = sousCompetencesArray.reduce(
      (sum, sc) => sum + sc.scoreObtenu,
      0,
    );
    const scoreMax = sousCompetencesArray.reduce(
      (sum, sc) => sum + sc.scoreMax,
      0,
    );
    // Score pondéré de la compétence = score brut × poids de la compétence
    const scorePondere = round2(scoreObtenu * compData.poids);
    const maxPondere = round2(scoreMax * compData.poids);

    return {
      nom: nomComp,
      poids: compData.poids,
      scoreObtenu,
      scoreMax,
      scorePondere,
      maxPondere,
      sousCompetences: sousCompetencesArray,
    };
  });

  // Calculer les scores globaux bruts
  const scoreGlobalBrut = competences.reduce(
    (sum, c) => sum + c.scoreObtenu,
    0,
  );
  const scoreGlobalMaxBrut = competences.reduce(
    (sum, c) => sum + c.scoreMax,
    0,
  );

  // Calculer les scores globaux pondérés (avec poids des compétences)
  // scorePondere inclut déjà le poids de la compétence, on fait simplement la somme
  const scoreGlobalObtenu = round2(
    competences.reduce((sum, c) => sum + c.scorePondere, 0),
  );
  const scoreGlobalMax = round2(
    competences.reduce((sum, c) => sum + c.maxPondere, 0),
  );

  return {
    scoreGlobalObtenu,
    scoreGlobalMax,
    scoreGlobalBrut,
    scoreGlobalMaxBrut,
    competences,
    isHierarchical: true,
  };
}

/**
 * Calcule les scores avec structure plate (ancien format sans sous-compétences)
 */
function calculerScoresFlat(
  questionsEtat: EntStructQuestionEtat[],
): ScoresCalcules {
  // Map pour grouper les critères et poids par compétence
  const competencesMap = new Map<
    string,
    { criteres: CritereDetail[]; poids: number }
  >();
  const ordreCompetences: string[] = [];

  // Parcourir toutes les questions et leurs critères
  for (const question of questionsEtat) {
    const competence = question.competence;

    // Garder l'ordre d'apparition
    if (!competencesMap.has(competence)) {
      competencesMap.set(competence, {
        criteres: [],
        poids: question.competence_weight ?? 1,
      });
      ordreCompetences.push(competence);
    }

    // Ajouter les critères à la compétence
    const compData = competencesMap.get(competence)!;
    for (const critere of question.criteria) {
      compData.criteres.push({
        criteriaId: critere.criteriaId,
        description: critere.description,
        points: critere.points,
        covered: critere.covered,
        asked: critere.asked,
        questionId: question.questionId,
      });
    }
  }

  // Calculer les scores par compétence avec pondération
  const competences: CompetenceScore[] = ordreCompetences.map((nom) => {
    const { criteres, poids } = competencesMap.get(nom)!;

    // Score obtenu = somme des points où covered=true
    const scoreObtenu = criteres
      .filter((c) => c.covered)
      .reduce((sum, c) => sum + c.points, 0);

    const scoreMax = criteres
      .filter((c) => c.points > 0)
      .reduce((sum, c) => sum + c.points, 0);

    const scorePondere = round2(scoreObtenu * poids);
    const maxPondere = round2(scoreMax * poids);

    return {
      nom,
      poids,
      scoreObtenu,
      scoreMax,
      scorePondere,
      maxPondere,
      sousCompetences: [], // Ancien format sans sous-compétences
      criteres,
    };
  });

  // Calculer les scores globaux bruts
  const scoreGlobalBrut = competences.reduce(
    (sum, c) => sum + c.scoreObtenu,
    0,
  );
  const scoreGlobalMaxBrut = competences.reduce(
    (sum, c) => sum + c.scoreMax,
    0,
  );

  // Calculer les scores globaux pondérés
  const scoreGlobalObtenu = round2(
    competences.reduce((sum, c) => sum + c.scorePondere, 0),
  );
  const scoreGlobalMax = round2(
    competences.reduce((sum, c) => sum + c.maxPondere, 0),
  );

  return {
    scoreGlobalObtenu,
    scoreGlobalMax,
    scoreGlobalBrut,
    scoreGlobalMaxBrut,
    competences,
    isHierarchical: false,
  };
}

function resolveQuestionsEtat(
  donnees: EntStructDonnees,
): EntStructQuestionEtat[] | undefined {
  if (donnees.resultats?.length) {
    try {
      const last = donnees.resultats[donnees.resultats.length - 1];
      const parsed = JSON.parse(last.data) as {
        result?: { questions?: EntStructQuestionEtat[] };
      };
      if (parsed.result?.questions?.length) return parsed.result.questions;
    } catch {
      // JSON.parse failed, fallback to questionsEtat
    }
  }
  return donnees.questionsEtat;
}

function calculerScores(
  questionsEtat: EntStructQuestionEtat[],
): ScoresCalcules {
  const questionsFiltrees = questionsEtat.filter(
    (q) => q.competence !== "Question neutre",
  );
  const isHierarchical = hasSousCompetences(questionsFiltrees);

  if (isHierarchical) {
    return calculerScoresHierarchique(questionsFiltrees);
  }
  return calculerScoresFlat(questionsFiltrees);
}

// ============================================
// FONCTIONS UTILITAIRES CONVERSATION
// ============================================

/**
 * Convertit et trie les messages de reponses par ordre chronologique
 */
function getMessagesFromReponses(
  reponses: Record<string, EntStructMessage>,
): EntStructMessage[] {
  return Object.entries(reponses)
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .map(([_, message]) => message);
}

export function calculerMoyenneCaracteres(
  reponses: Record<string, EntStructMessage>,
): { moyenne: number; nombreReponses: number } {
  const messages = getMessagesFromReponses(reponses);
  const reponsesUtilisateur = messages.filter((m) => m.role === "user");

  if (reponsesUtilisateur.length === 0) {
    return { moyenne: 0, nombreReponses: 0 };
  }

  const totalCaracteres = reponsesUtilisateur.reduce(
    (sum, m) => sum + m.content.replace(/ENT_FINISHED/g, "").trim().length,
    0,
  );

  return {
    moyenne: Math.round(totalCaracteres / reponsesUtilisateur.length),
    nombreReponses: reponsesUtilisateur.length,
  };
}

// ============================================
// STATISTIQUES DE TRANSCRIPTION
// ============================================

export interface TranscriptionStats {
  totalCaracteres: number;
  totalTranscrits: number;
  pourcentageGlobal: number;
  pourcentageMoyen: number;
  messagesVocaux: number;
  messagesTexte: number;
  messagesMixtes: number;
}

/**
 * Calcule les statistiques de transcription pour l'ensemble des réponses
 */
export function calculerStatsTranscription(
  reponses: Record<string, EntStructMessage>,
): TranscriptionStats {
  const messages = getMessagesFromReponses(reponses);
  const messagesUser = messages.filter((m) => m.role === "user");

  let totalCaracteres = 0;
  let totalTranscrits = 0;
  let sommePourcentages = 0;
  let messagesAvecMeta = 0;
  let messagesVocaux = 0;
  let messagesTexte = 0;
  let messagesMixtes = 0;

  for (const msg of messagesUser) {
    const length = msg.content.replace(/ENT_FINISHED/g, "").trim().length;
    totalCaracteres += length;

    if (msg.transcriptionMeta) {
      totalTranscrits += msg.transcriptionMeta.caracteresTranscrits;
      sommePourcentages += msg.transcriptionMeta.pourcentage;
      messagesAvecMeta++;

      if (msg.transcriptionMeta.pourcentage >= 80) {
        messagesVocaux++;
      } else if (msg.transcriptionMeta.pourcentage <= 20) {
        messagesTexte++;
      } else {
        messagesMixtes++;
      }
    } else {
      messagesTexte++;
    }
  }

  const pourcentageGlobal =
    totalCaracteres > 0
      ? Math.round((totalTranscrits / totalCaracteres) * 100)
      : 0;

  const pourcentageMoyen =
    messagesAvecMeta > 0 ? Math.round(sommePourcentages / messagesAvecMeta) : 0;

  return {
    totalCaracteres,
    totalTranscrits,
    pourcentageGlobal,
    pourcentageMoyen,
    messagesVocaux,
    messagesTexte,
    messagesMixtes,
  };
}

// ============================================
// COMPOSANTS D'AFFICHAGE
// ============================================

function ScoreGlobalHeader({
  obtenu,
  max,
  brutObtenu,
  brutMax,
  tricheDetectee,
  statsCheating,
}: {
  obtenu: number;
  max: number;
  brutObtenu: number;
  brutMax: number;
  tricheDetectee?: boolean;
  statsCheating?: CheatingStats;
}) {
  const pourcentage = max > 0 ? Math.round((obtenu / max) * 100) : 0;
  const [showTooltip, setShowTooltip] = useState(false);

  const raisonStriche: string[] = [];
  if (statsCheating) {
    if (statsCheating.nombrePastes > 0) {
      raisonStriche.push(
        `${statsCheating.nombrePastes} copier-coller${statsCheating.nombrePastes > 1 ? "s" : ""} détecté${statsCheating.nombrePastes > 1 ? "s" : ""}`,
      );
    }
    if (statsCheating.totalChangementsOnglet > 0) {
      raisonStriche.push(
        `${statsCheating.totalChangementsOnglet} changement(s) d'onglet`,
      );
    }
    if (statsCheating.scoreMoyen > 10) {
      raisonStriche.push(
        `Vitesse de frappe moyenne: ${statsCheating.scoreMoyen.toFixed(1)} car/s`,
      );
    }
  }

  return (
    <div className="p-6 bg-primary/10 rounded-lg text-center">
      <div className="flex items-center justify-center gap-2 mb-2">
        <Trophy className="h-6 w-6 text-primary" />
        <p className="text-sm font-medium text-muted-foreground">
          Score Global Pondéré
        </p>
      </div>
      <div className="flex flex-col items-center gap-2">
        <p className="text-4xl font-bold">{pourcentage}%</p>
        {tricheDetectee && (
          <div
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-orange-100 text-orange-700 text-sm font-medium whitespace-nowrap cursor-default">
              <AlertTriangle className="h-4 w-4" />
              Potentielle triche détectée
            </span>
            {showTooltip && statsCheating && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 bg-popover border rounded-md p-3 shadow-lg min-w-[260px] text-sm">
                <p className="font-semibold text-orange-700 mb-2">
                  Raisons de la détection
                </p>
                <ul className="space-y-1.5 text-xs text-muted-foreground">
                  {statsCheating.nombrePastes > 0 && (
                    <li className="flex items-center gap-2 text-red-500 font-medium">
                      <ClipboardPaste className="h-3 w-3" />
                      {statsCheating.nombrePastes} copier-coller
                      {statsCheating.nombrePastes > 1 ? "s" : ""} détecté
                      {statsCheating.nombrePastes > 1 ? "s" : ""}
                    </li>
                  )}
                  {statsCheating.totalChangementsOnglet > 0 && (
                    <li className="flex items-center gap-2">
                      <ArrowRightLeft className="h-3 w-3" />
                      {statsCheating.totalChangementsOnglet} changement(s)
                      d'onglet
                    </li>
                  )}
                  {statsCheating.scoreMoyen > 10 && (
                    <li className="flex items-start gap-2 text-orange-500">
                      <Type className="h-3 w-3 shrink-0" />
                      <span>
                        Vitesse de frappe suspecte (moyenne:{" "}
                        {statsCheating.scoreMoyen.toFixed(1)} car/s)
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-2">
        Score brut : {brutObtenu}/{brutMax} points
      </p>
    </div>
  );
}

function TableCriteres({ criteres }: { criteres: CritereDetail[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-16">Statut</TableHead>
          <TableHead>Description</TableHead>
          <TableHead className="w-24 text-right">Points</TableHead>
          <TableHead className="w-28">Type</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {criteres.map((critere) => (
          <TableRow
            key={critere.criteriaId}
            className={!critere.covered ? "opacity-60" : ""}
          >
            <TableCell>
              {critere.covered ? (
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
              ) : (
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100">
                  <X className="h-4 w-4 text-gray-400" />
                </div>
              )}
            </TableCell>
            <TableCell className="font-medium">{critere.description}</TableCell>
            <TableCell
              className={`text-right font-semibold ${
                critere.covered
                  ? critere.points > 0
                    ? "text-green-600"
                    : "text-red-600"
                  : "text-gray-400"
              }`}
            >
              {critere.points > 0 ? `+${critere.points}` : critere.points} pts
            </TableCell>
            <TableCell>
              {critere.points >= 0 && (critere.covered || critere.asked) && (
                <Badge
                  variant={critere.asked ? "default" : "secondary"}
                  className="text-xs"
                >
                  {critere.asked ? "Relance" : "Spontané"}
                </Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CriteresParQuestion({
  criteres,
  questionsMap,
}: {
  criteres: CritereDetail[];
  questionsMap: Map<string, string>;
}) {
  const groups: {
    questionId: string;
    questionText: string;
    criteres: CritereDetail[];
  }[] = [];
  const seen = new Map<string, number>();

  for (const critere of criteres) {
    if (!seen.has(critere.questionId)) {
      seen.set(critere.questionId, groups.length);
      groups.push({
        questionId: critere.questionId,
        questionText: questionsMap.get(critere.questionId) ?? "",
        criteres: [critere],
      });
    } else {
      groups[seen.get(critere.questionId)!]!.criteres.push(critere);
    }
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.questionId}>
          {group.questionText && (
            <p className="text-sm font-medium text-foreground/80 mb-2 px-1 py-1.5 bg-muted/60 rounded border-l-2 border-primary/40 italic">
              {group.questionText}
            </p>
          )}
          <TableCriteres criteres={group.criteres} />
        </div>
      ))}
    </div>
  );
}

function SousCompetenceCard({
  sousCompetence,
  questionsMap,
}: {
  sousCompetence: SousCompetenceScore;
  questionsMap: Map<string, string>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const pourcentage =
    sousCompetence.scoreMax > 0
      ? Math.round((sousCompetence.scoreObtenu / sousCompetence.scoreMax) * 100)
      : 0;

  // Ne pas afficher les sous-compétences vides (nom vide ou "Général" sans critères)
  if (
    sousCompetence.nom === "" ||
    (sousCompetence.nom === "Général" && sousCompetence.criteres.length === 0)
  ) {
    return null;
  }

  return (
    <div className="ml-4 mt-2 border-l-2 border-muted rounded-lg p-3 bg-muted/30">
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-muted-50 transition-colors select-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="font-medium text-sm">{sousCompetence.nom}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${pourcentage}%` }}
            />
          </div>
          <Badge
            variant="secondary"
            className="text-sm min-w-[80px] flex justify-center"
          >
            {sousCompetence.scoreObtenu}/{sousCompetence.scoreMax}
          </Badge>
        </div>
      </div>
      {isOpen && (
        <div className="mt-3">
          <CriteresParQuestion
            criteres={sousCompetence.criteres}
            questionsMap={questionsMap}
          />
        </div>
      )}
    </div>
  );
}

function CompetenceCard({
  competence,
  isHierarchical,
  questionsMap,
}: {
  competence: CompetenceScore;
  isHierarchical: boolean;
  questionsMap: Map<string, string>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Calculer le pourcentage pour la barre de progression (basé sur le score brut)
  const pourcentage =
    competence.scoreMax > 0
      ? Math.round((competence.scoreObtenu / competence.scoreMax) * 100)
      : 0;

  return (
    <Card>
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors select-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            {isOpen ? (
              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
            ) : (
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
            )}
            <div className="flex flex-col gap-1 min-w-0">
              <CardTitle className="text-lg">{competence.nom}</CardTitle>
              <Badge variant="secondary" className="text-xs w-fit">
                Poids: {competence.poids}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {/* Barre de progression miniature */}
            <div className="hidden sm:block w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${pourcentage}%` }}
              />
            </div>
            <Badge
              variant="outline"
              className="text-base px-3 py-1 min-w-20 flex justify-center"
            >
              {pourcentage}%
            </Badge>
            <span className="text-sm text-muted-foreground whitespace-nowrap min-w-24 text-left">
              ({competence.scoreObtenu}/{competence.scoreMax} points)
            </span>
          </div>
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent className="pt-0">
          {/* Affichage hiérarchique avec sous-compétences */}
          {isHierarchical && competence.sousCompetences ? (
            <div className="space-y-3">
              {competence.sousCompetences.map((sousComp, idx) => (
                <SousCompetenceCard
                  key={idx}
                  sousCompetence={sousComp}
                  questionsMap={questionsMap}
                />
              ))}
            </div>
          ) : null}
          {/* Affichage plate (ancien format) */}
          {!isHierarchical && competence.criteres && (
            <CriteresParQuestion
              criteres={competence.criteres}
              questionsMap={questionsMap}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}

function MessageAucuneDonnee() {
  return (
    <div className="p-8 text-center text-muted-foreground">
      <p>Aucune donnée de score disponible pour cet entretien.</p>
      <p className="text-sm mt-2">
        L'entretien n'a peut-être pas encore généré de données d'évaluation.
      </p>
    </div>
  );
}

function CheatingIndicator({ meta }: { meta: CheatingMeta }) {
  const interpretation = interpreterScore(meta.scoreSuspicion);

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${interpretation.couleur} bg-muted`}
    >
      <AlertTriangle className="h-3 w-3" />
      <span>({interpretation.niveau})</span>
    </div>
  );
}

function CheatingTooltip({
  meta,
  anchorRect,
}: {
  meta: CheatingMeta;
  anchorRect: DOMRect;
}) {
  const interpretation = interpreterScore(meta.scoreSuspicion);

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: anchorRect.bottom + 4,
        right: window.innerWidth - anchorRect.right,
        zIndex: 9999,
      }}
      className="bg-popover border rounded-md p-3 shadow-lg min-w-55 text-sm"
    >
      <div className="space-y-2">
        <div className="flex items-center justify-between font-semibold">
          <span className={interpretation.couleur}>
            {interpretation.niveau}
          </span>
          <span className="text-xs text-muted-foreground">
            {interpretation.description}
          </span>
        </div>
        <div className="border-t pt-2 space-y-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-3 w-3" />
            <span>Changements onglet: {meta.changementsOnglet}</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="h-3 w-3" />
            <span>
              Temps hors page: {(meta.tempsHorsPage / 1000).toFixed(1)}s
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Timer className="h-3 w-3" />
            <span>
              Temps rédaction: {(meta.tempsRedaction / 1000).toFixed(1)}s
            </span>
          </div>
          {meta.pasteDetecte && (
            <div className="flex items-center gap-2 text-red-500 font-medium">
              <ClipboardPaste className="h-3 w-3" />
              <span>Copier-coller détecté sur cette réponse</span>
            </div>
          )}
          {meta.vitesseFrappe > 10 && (
            <div className="flex items-center gap-2 text-orange-500">
              <Type className="h-3 w-3" />
              <span>
                Vitesse anormale: {meta.vitesseFrappe.toFixed(1)} car/s
              </span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Composant pour afficher un message de la conversation
 */
function MessageBubble({ message }: { message: EntStructMessage }) {
  const isAssistant = message.role === "assistant";
  const [showTooltip, setShowTooltip] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const getMessageType = () => {
    if (isAssistant || !message.transcriptionMeta) return "text";
    const pct = message.transcriptionMeta.pourcentage;
    if (pct >= 80) return "voice";
    if (pct >= 20) return "mixed";
    return "text";
  };

  const messageType = getMessageType();

  return (
    <div
      className={`flex min-w-0 ${isAssistant ? "justify-start" : "justify-end"} mb-3`}
    >
      <div
        className={`max-w-[85%] min-w-0 p-3 rounded-lg ${
          isAssistant
            ? "bg-muted text-muted-foreground"
            : "bg-primary/10 text-primary"
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          {isAssistant ? (
            <Bot className="h-4 w-4" />
          ) : (
            <User className="h-4 w-4" />
          )}
          <Badge
            variant={isAssistant ? "secondary" : "default"}
            className="text-xs pointer-events-none"
          >
            {isAssistant ? "Assistant" : "Candidat"}
          </Badge>
          {!isAssistant && (
            <>
              {messageType === "voice" && (
                <Badge
                  variant="default"
                  className="text-xs bg-blue-500 ml-1 pointer-events-none"
                >
                  <Mic className="h-3 w-3 mr-1" /> Vocal
                </Badge>
              )}
              {messageType === "mixed" && (
                <Badge
                  variant="secondary"
                  className="text-xs ml-1 pointer-events-none"
                >
                  <Mic className="h-3 w-3 mr-1" />
                  Mixte ({message.transcriptionMeta?.pourcentage}%)
                </Badge>
              )}
              {messageType === "text" && !message.transcriptionMeta && (
                <Badge variant="outline" className="text-xs ml-1">
                  <Type className="h-3 w-3 mr-1" /> Texte
                </Badge>
              )}
              {message.cheatingMeta && (
                <div
                  ref={triggerRef}
                  className="ml-auto"
                  onMouseEnter={() => {
                    if (triggerRef.current) {
                      setAnchorRect(triggerRef.current.getBoundingClientRect());
                    }
                    setShowTooltip(true);
                  }}
                  onMouseLeave={() => {
                    setShowTooltip(false);
                    setAnchorRect(null);
                  }}
                >
                  <CheatingIndicator meta={message.cheatingMeta} />
                  {showTooltip && anchorRect && (
                    <CheatingTooltip
                      meta={message.cheatingMeta}
                      anchorRect={anchorRect}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <p className="whitespace-pre-wrap break-words text-sm">
          {message.content.replace(/ENT_FINISHED/g, "").trim()}
        </p>
      </div>
    </div>
  );
}

/**
 * Composant dropdown pour afficher la transcription de la conversation
 */
function ConversationDropdown({
  reponses,
}: {
  reponses: Record<string, EntStructMessage>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const messages = getMessagesFromReponses(reponses);

  if (messages.length === 0) return null;

  return (
    <Card className="w-full">
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors select-none w-full"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isOpen ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">
              Transcription de l'entretien
            </CardTitle>
          </div>
          <Badge variant="outline" className="text-base px-3 py-1">
            {messages.length} messages
          </Badge>
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent className="overflow-hidden w-full">
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} />
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export function EntStructResults({
  donnees,
  candidatInfo,
}: EntStructResultsProps) {
  const [voirPlus, setVoirPlus] = useState(false);

  if (donnees.type !== "ent-struct") {
    return <div>Type de données incorrect</div>;
  }

  const questionsEtat = (() => {
    if (donnees.resultats?.length) {
      try {
        const parsed = JSON.parse(
          donnees.resultats[donnees.resultats.length - 1].data,
        );
        if (parsed.result?.questions?.length)
          return parsed.result.questions as EntStructQuestionEtat[];
      } catch {}
    }
    return donnees.questionsEtat;
  })();

  if (!questionsEtat || questionsEtat.length === 0) {
    return <MessageAucuneDonnee />;
  }

  const scores = calculerScores(questionsEtat);
  const questionsMap = new Map(
    questionsEtat.map((q) => [q.questionId, q.question_text]),
  );
  const messagesReponses = donnees.reponses
    ? getMessagesFromReponses(donnees.reponses)
    : [];
  const statsCheating = calculerStatsCheating(messagesReponses);
  const tricheDetectee = estSuspect(messagesReponses);

  const statsTranscription = donnees.reponses
    ? calculerStatsTranscription(donnees.reponses)
    : null;
  const statsCaracteres = donnees.reponses
    ? calculerMoyenneCaracteres(donnees.reponses)
    : null;

  const criteresApresRelance = scores.competences.reduce(
    (sum, c) =>
      sum +
      (c.criteres?.filter((cr) => cr.asked && cr.covered).length ??
        c.sousCompetences.reduce(
          (s, sc) =>
            s + sc.criteres.filter((cr) => cr.asked && cr.covered).length,
          0,
        )),
    0,
  );
  const criteresSpontanes = scores.competences.reduce(
    (sum, c) =>
      sum +
      (c.criteres?.filter((cr) => !cr.asked && cr.covered).length ??
        c.sousCompetences.reduce(
          (s, sc) =>
            s + sc.criteres.filter((cr) => !cr.asked && cr.covered).length,
          0,
        )),
    0,
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">Entretien Structuré</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {candidatInfo.prenom} {candidatInfo.nom} — {candidatInfo.email}
            </div>
          </div>
          <Badge variant="success">Terminée</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {candidatInfo.tempsPasse !== undefined && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Temps passé</span>
                <p className="font-medium">
                  {formatDuration(candidatInfo.tempsPasse)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateDebut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de début</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateDebut)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de fin</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateFin)}
                </p>
              </div>
            </div>
          )}
          {statsTranscription && statsTranscription.pourcentageGlobal > 50 && (
            <span
              className="flex items-center gap-2 cursor-default"
              title={`${statsTranscription.pourcentageGlobal}% du contenu transcrit`}
            >
              <Mic className="h-4 w-4 text-blue-500 pointer-events-none" />
              <span className="text-blue-600 font-medium">
                Transcription utilisée
              </span>
            </span>
          )}
        </div>

        {voirPlus && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Type className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">
                  Taille moyenne d'une réponse
                </span>
                <p className="font-medium">
                  {statsCaracteres?.moyenne} caractères
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-blue-500" />
              <div>
                <span className="text-muted-foreground">
                  Critères couverts après relance
                </span>
                <p className="font-medium text-blue-600">
                  {criteresApresRelance}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-emerald-500" />
              <div>
                <span className="text-muted-foreground">
                  Critères couverts spontanément
                </span>
                <p className="font-medium text-emerald-600">
                  {criteresSpontanes}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ClipboardPaste
                className={`h-4 w-4 ${statsCheating.nombrePastes > 0 ? "text-red-500" : "text-muted-foreground"}`}
              />
              <div>
                <span className="text-muted-foreground">
                  Copier-coller détectés
                </span>
                <p
                  className={`font-medium ${statsCheating.nombrePastes > 0 ? "text-red-600" : ""}`}
                >
                  {statsCheating.nombrePastes}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setVoirPlus(!voirPlus)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            Voir plus
            {voirPlus ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Card Résultats bruts */}
      <Card className="mb-6 w-full overflow-hidden">
        <CardHeader>
          <CardTitle>Résultats bruts</CardTitle>
          <CardDescription>Données brutes de l'évaluation</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <div className="space-y-6 w-full max-w-full min-w-0">
            <ScoreGlobalHeader
              obtenu={scores.scoreGlobalObtenu}
              max={scores.scoreGlobalMax}
              brutObtenu={scores.scoreGlobalBrut}
              brutMax={scores.scoreGlobalMaxBrut}
              tricheDetectee={tricheDetectee}
              statsCheating={statsCheating}
            />

            {/* Liste des compétences */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Détail par compétence</h3>
              {scores.competences.map((competence, index) => (
                <CompetenceCard
                  key={index}
                  competence={competence}
                  isHierarchical={scores.isHierarchical}
                  questionsMap={questionsMap}
                />
              ))}
            </div>

            {/* Transcription de la conversation */}
            {donnees.reponses && Object.keys(donnees.reponses).length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Transcription</h3>
                <ConversationDropdown reponses={donnees.reponses} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
